import type { Language } from '../schemas/simulation'
import type { CertItem, CertReadiness, CertReadinessTarget } from './readiness'
import { readinessHeadline } from './readiness'
import { CERT_RULE_COPY, CERT_VARIANT_COPY, type RuleCopy } from './rule-copy'
import type { CertWho } from './rules'
import { SUBSTITUTIONS, type SubstitutionKey } from './substitutions'
import { CERT_RULES_READ_DATE, type CertTarget, type HalalMarket } from './targets'

/**
 * 인증 준비도 화면 문구 (2026-11-01, 설계 4절). 고르기 카드·결과 카드·상세 시트·복사 글·견적 미리 채우기가 모두 여기서 나온다.
 * 4개 언어 고정 사전이다 (실행 중 AI 번역 없음). 규칙별 문구는 rule-copy.ts, 대체 원료 문장은 substitutions.ts.
 *
 * 쓰는 말: "○○ 기준에 맞춘 설계", "설계에 반영됨", "확인 필요", "공장·서류로 확인", "고칠 것", "보완할 것", "자가 점검"
 * 쓰지 않는 말: CERT_FORBIDDEN_WORDS (인증됨·합격·통과·certified·guaranteed …). 근거: 식품표시광고법 시행령 별표1 4호 마목,
 * 식품위생법 제48조⑨ — 인증과 관련해 사실과 다른 표시는 거짓·과장이다. 테스트가 이 파일과 rule-copy.ts·substitutions.ts 의
 * 모든 문구에서 금지어와 판매 문구 인증 단정 정규식(unsupported-claims.ts)을 돌린다.
 */

/** 화면·테스트가 같이 쓰는 금지어 (설계 4-1). 4개 언어 문구 어디에도 없어야 한다 */
export const CERT_FORBIDDEN_WORDS: Record<Language, string[]> = {
  ko: [
    '인증됨',
    '인증 완료',
    '인증 보장',
    '인증 가능',
    '합격',
    '통과',
    '통과 확률',
    '승인',
    '적합 판정',
    '% 준비',
    '보장',
    '인증받',
    '인증을 받',
    '획득',
  ],
  en: ['certified', 'compliant', 'approved', 'guaranteed', 'pass rate', 'guarantee', 'passed'],
  // '承認' 은 넣지 않는다 — '相互承認'(상호인정)이 기관 안내에 정당하게 쓰인다
  ja: ['認証済み', '認定', '適合', '保証', '合格', '認証取得'],
  zh: ['认证通过', '符合', '保证', '合格', '通过认证', '已认证', '获得认证', '批准'],
}

const L = (ko: string, en: string, ja: string, zh: string): Record<Language, string> => ({
  ko,
  en,
  ja,
  zh,
})

export const CERT_TARGET_LABELS: Record<CertTarget, Record<Language, string>> = {
  halal: L('할랄', 'Halal', 'ハラール', '清真'),
  haccp: L('해썹(HACCP)', 'HACCP', 'HACCP', 'HACCP'),
  fda: L('미국 판매(FDA)', 'US sale (FDA)', '米国販売(FDA)', '美国销售（FDA）'),
  china: L('중국 수출', 'Export to China', '中国輸出', '出口中国'),
}

export const HALAL_MARKET_LABELS: Record<HalalMarket, Record<Language, string>> = {
  undecided: L('아직 모름', 'Not decided yet', 'まだ決めていない', '尚未确定'),
  kr: L('국내', 'Korea', '韓国国内', '韩国国内'),
  my: L('말레이시아', 'Malaysia', 'マレーシア', '马来西亚'),
  id: L('인도네시아', 'Indonesia', 'インドネシア', '印度尼西亚'),
  ae: L('UAE·중동', 'UAE & Middle East', 'UAE・中東', '阿联酋·中东'),
}

/**
 * 판매국을 고르면 보이는 인정 기관 안내 (모두 1차 출처, research/halal.md 1절·6-4, 2026-11-01 확인).
 * 앱은 공장의 인증기관을 모른다. 그래서 "이 나라 목록에 있는 한국 기관" 만 목록 날짜와 함께 말하고, 공장 기관은 ⚠️ 항목으로
 * 확인하게 한다. 목록에 없는 기관을 이름으로 짚지 않는다 (목록은 바뀌고, 특정 회사를 가리킬 수 있다).
 * - 말레이시아 JAKIM 인정 해외기관 목록 2026-08-21판: KMF 72번, KHA 73번
 * - 인도네시아 BPJPH: KMF·KHA 2023 상호인정 서명(지금 유효한지는 미확인), GL Halal Center Korea 2026-05-20 인정협약(RA).
 *   수입 식품 의무는 PP 40/2026 제160조(3)으로 2028-10-18 부터
 * - UAE MoIAT 등록 목록(2026-11-01, 활성 98곳): 한국 기관 없음
 * - 국내: 할랄 인증은 법정 제도가 아니다. 표시는 인증서가 있을 때만 한다
 */
export const HALAL_MARKET_NOTES: Record<HalalMarket, Record<Language, string>> = {
  undecided: L(
    '판매국이 정해지면 그 나라 인정 목록에서 공장 인증기관을 확인하세요. 지금은 말레이시아·인도네시아·UAE 항목을 모두 보여 드려요.',
    "Once you pick a sales country, check the factory's certification body against that country's list. For now we show the Malaysia, Indonesia and UAE items together.",
    '販売国が決まったら、その国の一覧で工場の認証機関を確認してください。今はマレーシア・インドネシア・UAEの項目をすべて表示します。',
    '确定销售国后，请在该国名单中确认工厂的认证机构。目前同时显示马来西亚、印度尼西亚和阿联酋的项目。',
  ),
  kr: L(
    '국내에는 할랄 표시를 정한 법정 제도가 없어요. 표시는 공장이 받은 인증서와 그 제품 목록이 있을 때만 하세요.',
    'Korea has no statutory halal labelling scheme. Label only when the factory holds a certificate whose product list includes this product.',
    '韓国にはハラール表示を定めた法定制度がありません。工場が受けた認証書とその製品一覧があるときだけ表示してください。',
    '韩国没有规定清真标示的法定制度。只有在工厂持有证书且产品清单包含本产品时才可标示。',
  ),
  my: L(
    '말레이시아 JAKIM 인정 목록(2026-08-21)의 한국 기관은 KMF(72번)·KHA(73번)뿐이에요. 이 목록에 없는 기관의 인증서로는 말레이시아용 할랄 표시를 할 수 없어요.',
    "On Malaysia's JAKIM list (2026-08-21) the Korean bodies are only KMF (No. 72) and KHA (No. 73). A certificate from a body not on this list cannot be used for a halal label in Malaysia.",
    'マレーシアJAKIMの一覧(2026-08-21)にある韓国の機関はKMF(72番)・KHA(73番)のみです。この一覧にない機関の認証書では、マレーシア向けのハラール表示ができません。',
    '马来西亚JAKIM名单（2026-08-21）中的韩国机构只有KMF(第72号)和KHA(第73号)。不在该名单上的机构颁发的证书不能用于马来西亚的清真标示。',
  ),
  id: L(
    '2028-10-18부터 인도네시아로 들여가는 식품은 BPJPH와 상호인정한 기관의 인증서가 있어야 해요. KMF·KHA는 2023년에 서명했지만 지금도 유효한지는 확인되지 않았어요. GL Halal Center Korea는 2026-05-20에 BPJPH와 인정협약을 맺었는데, 지금 해외 인증서 등록에 쓸 수 있는지는 확인해야 해요.',
    'From 2028-10-18, food imported into Indonesia needs a certificate from a body with a BPJPH mutual recognition agreement. KMF and KHA signed in 2023, but whether it is still in force is unconfirmed. GL Halal Center Korea signed a recognition agreement with BPJPH on 2026-05-20; whether it can be used for foreign certificate registration now still needs checking.',
    '2028-10-18から、インドネシアに輸入する食品にはBPJPHと相互承認を結んだ機関の認証書が必要です。KMF・KHAは2023年に署名しましたが、現在も有効かは未確認です。GL Halal Center Koreaは2026-05-20にBPJPHと協定を結びましたが、今の海外認証書の登録に使えるかは確認が必要です。',
    '自2028-10-18起，进口到印度尼西亚的食品需要与BPJPH签订互认协议的机构的证书。KMF和KHA于2023年签署，但目前是否仍有效尚未确认。GL Halal Center Korea于2026-05-20与BPJPH签订了认可协议，目前能否用于海外证书登记仍需确认。',
  ),
  ae: L(
    'UAE MoIAT 등록 목록(2026-11-01)에는 한국 기관이 없어요. 국내 기관 인증서만으로는 UAE에서 받아들여지기 어려워요.',
    'The UAE MoIAT register (2026-11-01) lists no Korean body. A certificate from a Korean body alone is unlikely to be accepted in the UAE.',
    'UAE MoIATの登録一覧(2026-11-01)に韓国の機関はありません。韓国の機関の認証書だけではUAEで受け入れられにくいです。',
    '阿联酋MoIAT注册名单（2026-11-01）中没有韩国机构。仅凭韩国机构的证书在阿联酋很难被接受。',
  ),
}

export const CERT_WHO_LABELS: Record<CertWho, Record<Language, string>> = {
  // ✅·❌ 항목의 '설계' 는 사용자가 앱에서 고치는 것이다 (⚠️ 항목에는 '설계' 가 오지 않는다 — readiness.ts whoFor)
  design: L('나(설계)', 'You (design)', 'ご自身(設計)', '您（设计）'),
  factory: L('공장', 'Factory', '工場', '工厂'),
  importer: L('수입자', 'Importer', '輸入者', '进口商'),
  seller: L('판매자', 'Seller', '販売者', '销售方'),
  expert: L('전문가', 'Expert', '専門家', '专家'),
}

type Fill = (vars: Record<string, string | number>) => string
const fill =
  (template: string): Fill =>
  (vars) =>
    template.replace(/\{(\w+)\}/g, (_m, k: string) => String(vars[k] ?? ''))

export interface CertUiCopy {
  pickerTitle: string
  pickerHelp: string
  halalSub: string
  /** 분석 모드: 사용자 재료를 바꾸지 않고 찾아 알려 준다 (프롬프트의 ANALYZE_LINE) */
  halalSubAnalyze: string
  haccpSub: string
  haccpSubAnalyze: string
  haccpDisabled: string
  halalMarketQ: string
  halalMarketHelp: string
  cardTitle: string
  lineOk: Fill
  /** 앱이 판정한 항목이 없을 때 (일본어·중국어처럼 사전이 일부뿐이면) — '기준에 맞춘 설계' 라고 말하지 않는다 */
  lineUnchecked: Fill
  lineImprove: Fill
  lineFix: Fill
  checked: Fill
  more: string
  foot: string
  stale: string
  sheetTitle: Fill
  basis: Fill
  secFix: string
  secReflected: string
  secDocs: string
  secReview: string
  secNa: Fill
  who: string
  byAi: string
  staleBadge: string
  stepLabel: Fill
  btnFix: string
  btnShareDocs: string
  shared: string
  notice: Fill
  halalNotice: string
  fixHeader: Fill
  docsHeader: Fill
  docsIngredients: Fill
  docsFooter: string
  quoteHalal: Fill
  quoteHaccp: Fill
}

export const CERT_UI_COPY: Record<Language, CertUiCopy> = {
  ko: {
    pickerTitle: '판매·인증 목표 (선택)',
    pickerHelp:
      '고르면 그 기준에 맞춰 배합과 공정을 짜고, 무엇이 남았는지 알려 드려요. 인증 여부는 제조 공장이 신청하고 인증기관이 심사해 정합니다.',
    halalSub: '돼지·술 원료를 빼고, 서류로 증빙할 원료를 표시해요',
    halalSubAnalyze: '돼지·술 원료를 찾아 알려 드리고, 서류로 증빙할 원료를 표시해요',
    haccpSub: '식품공전 기준과 관리점(가열·금속검출)을 공정에 넣어요',
    haccpSubAnalyze: '식품공전 기준과 관리점(가열·금속검출)에 비춰 공정을 점검해요',
    haccpDisabled: '해썹은 국내 판매 기준이라 미국 판매 제품에는 적용하지 않아요',
    halalMarketQ: '어느 나라에서 팔 계획인가요?',
    halalMarketHelp: "나라마다 인정하는 인증기관이 달라요. 모르면 '아직 모름'으로 두세요.",
    cardTitle: '인증 준비도',
    lineOk: fill('{target} 기준에 맞춘 설계 · 확인 필요 {n}개'),
    lineUnchecked: fill('{target} 기준 · 앱이 판정한 항목 없음 · 확인 필요 {n}개'),
    lineImprove: fill('{target} 기준에서 보완할 것 {k}개 · 확인 필요 {n}개'),
    lineFix: fill('{target} 기준에서 고칠 것 {k}개 · 확인 필요 {n}개'),
    checked: fill('앱이 점검한 {a}개 중 {b}개 반영'),
    more: '자세히 보기',
    foot: '자가 점검 결과이며 인증 결과가 아니에요.',
    stale: '재료나 공정을 바꿔서 일부 항목을 다시 확인해야 해요',
    sheetTitle: fill('{target} 준비도'),
    basis: fill('규칙 기준일 {date} · 이 제품에 해당하는 규칙 {n}개'),
    secFix: '설계에서 고칠 것',
    secReflected: '설계에 반영됨 (앱이 확인함)',
    secDocs: '공장·서류로 확인할 것',
    secReview: '전문가 확인이 필요한 것',
    secNa: fill('이 제품에 해당하지 않는 규칙 {n}개'),
    who: '누가',
    byAi: 'AI 확인',
    staleBadge: '다시 확인',
    stepLabel: fill('{n}번째 공정'),
    btnFix: '이렇게 고쳐 줘',
    btnShareDocs: '공장에 받을 서류 목록 공유',
    shared: '공유했어요.',
    notice: fill(
      "이 화면은 RecipeCat이 제품 설계를 공개된 법령·기준과 대조한 자가 점검입니다. 인증 여부는 제조 공장이 신청하고 인증기관(할랄은 각 할랄 인증기관, 해썹은 한국식품안전관리인증원)이 심사해 정합니다. ✅는 '설계에 반영됨'이라는 뜻이고 심사 결과가 아닙니다. 인증서를 받기 전에는 라벨·상세페이지에 인증 마크나 '인증' 표현을 쓰지 마세요. 규칙은 {date} 기준이며 바뀔 수 있습니다.",
    ),
    halalNotice:
      '할랄은 나라마다 인정하는 인증기관이 다릅니다. 판매할 나라가 정해지면 그 나라 인정 목록에서 공장 인증기관을 확인하세요.',
    fixHeader: fill('{target} 기준에 맞게 아래를 고쳐 줘.'),
    docsHeader: fill('{product} · {target} 준비 — 공장에 받을 서류'),
    docsIngredients: fill('증빙할 원료: {names}'),
    docsFooter: 'RecipeCat 자가 점검으로 만든 목록이에요. 인증 결과가 아니에요.',
    quoteHalal: fill('할랄(판매국: {market})'),
    quoteHaccp: fill('HACCP(식품유형: {type})'),
  },
  en: {
    pickerTitle: 'Sales & certification goals (optional)',
    pickerHelp:
      'Pick one and we design the recipe and process to that standard and show what is left to do. Whether a certificate is issued is decided by the certification body after the factory applies and is audited.',
    halalSub: 'Leaves out pork and alcohol and marks ingredients that need papers',
    halalSubAnalyze: 'Finds pork and alcohol ingredients and marks ingredients that need papers',
    haccpSub:
      'Builds Korean Food Code standards and control points (heating, metal detection) into the process',
    haccpSubAnalyze:
      'Checks the process against Korean Food Code standards and control points (heating, metal detection)',
    haccpDisabled:
      'HACCP is a Korean sales standard, so it does not apply to products sold in the US',
    halalMarketQ: 'Which country do you plan to sell in?',
    halalMarketHelp:
      "Each country recognises different certification bodies. If you're not sure, leave it at 'Not decided yet'.",
    cardTitle: 'Certification readiness',
    lineOk: fill('Designed to {target} criteria · {n} to confirm'),
    lineUnchecked: fill('{target}: no items checked by the app · {n} to confirm'),
    lineImprove: fill('{k} to improve for {target} · {n} to confirm'),
    lineFix: fill('{k} to fix for {target} · {n} to confirm'),
    checked: fill('{b} of {a} app checks reflected in the design'),
    more: 'See details',
    foot: 'Self-check, not a certification result.',
    stale: 'You changed ingredients or steps, so some items need checking again',
    sheetTitle: fill('{target} readiness'),
    basis: fill('Rules as of {date} · {n} rules apply to this product'),
    secFix: 'Fix in the design',
    secReflected: 'Reflected in the design (checked by the app)',
    secDocs: 'Confirm with the factory and papers',
    secReview: 'Needs an expert check',
    secNa: fill('{n} rules do not apply to this product'),
    who: 'Who',
    byAi: 'AI check',
    staleBadge: 'Re-check',
    stepLabel: fill('Step {n}'),
    btnFix: 'Fix it like this',
    btnShareDocs: 'Share the list of papers to get from the factory',
    shared: 'Shared.',
    notice: fill(
      "This screen is a self-check comparing your product design with published laws and standards. Whether a certificate is issued is decided by the certification body (halal: the halal certification body; HACCP: the Korea Agency of HACCP Accreditation and Services) after the factory applies and is audited. ✅ means 'reflected in the design', not an audit result. Do not put certification marks or wording on labels or product pages before the certificate is issued. Rules as of {date}; they may change.",
    ),
    halalNotice:
      "Each country recognises different halal certification bodies. Once you pick a sales country, check the factory's body against that country's list.",
    fixHeader: fill('Please revise the following to meet the {target} criteria.'),
    docsHeader: fill('{product} · {target} preparation — papers to get from the factory'),
    docsIngredients: fill('Ingredients that need papers: {names}'),
    docsFooter: 'List made by a RecipeCat self-check. It is not a certification result.',
    quoteHalal: fill('Halal (sales country: {market})'),
    quoteHaccp: fill('HACCP (food type: {type})'),
  },
  ja: {
    pickerTitle: '販売・認証の目標(任意)',
    pickerHelp:
      '選ぶとその基準に合わせて配合と工程を組み、何が残っているかをお知らせします。認証の可否は、製造工場が申請し、認証機関が審査で決めます。',
    halalSub: '豚・酒の原料を外し、書類で証明する原料を示します',
    halalSubAnalyze: '豚・酒の原料を見つけてお知らせし、書類で証明する原料を示します',
    haccpSub: '韓国食品公典の基準と管理点(加熱・金属検出)を工程に入れます',
    haccpSubAnalyze: '韓国食品公典の基準と管理点(加熱・金属検出)に照らして工程を点検します',
    haccpDisabled: 'HACCPは韓国国内販売の基準なので、米国販売の製品には適用しません',
    halalMarketQ: 'どの国で販売する予定ですか?',
    halalMarketHelp:
      '国ごとに認める認証機関が異なります。わからなければ「まだ決めていない」のままにしてください。',
    cardTitle: '認証の準備度',
    lineOk: fill('{target}基準に合わせた設計・要確認{n}件'),
    lineUnchecked: fill('{target}基準・アプリが判定した項目なし・要確認{n}件'),
    lineImprove: fill('{target}基準で補うこと{k}件・要確認{n}件'),
    lineFix: fill('{target}基準で直すこと{k}件・要確認{n}件'),
    checked: fill('アプリが点検した{a}件中{b}件を反映'),
    more: '詳しく見る',
    foot: '自己点検の結果であり、認証の結果ではありません。',
    stale: '材料や工程を変えたため、一部の項目を再確認する必要があります',
    sheetTitle: fill('{target}の準備度'),
    basis: fill('規則基準日 {date}・この製品に該当する規則{n}件'),
    secFix: '設計で直すこと',
    secReflected: '設計に反映(アプリが確認)',
    secDocs: '工場・書類で確認すること',
    secReview: '専門家の確認が必要なこと',
    secNa: fill('この製品に該当しない規則{n}件'),
    who: '担当',
    byAi: 'AI確認',
    staleBadge: '再確認',
    stepLabel: fill('工程{n}'),
    btnFix: 'こう直して',
    btnShareDocs: '工場から受け取る書類一覧を共有',
    shared: '共有しました。',
    notice: fill(
      'この画面は、RecipeCatが製品設計を公開された法令・基準と照らし合わせた自己点検です。認証書が出るかどうかは、製造工場が申請し、認証機関(ハラールは各ハラール認証機関、HACCPは韓国食品安全管理認証院)が審査で決めます。✅は「設計に反映」という意味で、審査の結果ではありません。認証書を受ける前は、ラベル・商品ページに認証マークや「認証」の表現を使わないでください。規則は{date}時点のもので、変わることがあります。',
    ),
    halalNotice:
      'ハラールは国ごとに認める認証機関が異なります。販売国が決まったら、その国の一覧で工場の認証機関を確認してください。',
    fixHeader: fill('{target}の基準に合わせて、次の点を直してください。'),
    docsHeader: fill('{product}・{target}の準備 — 工場から受け取る書類'),
    docsIngredients: fill('証明が必要な原料: {names}'),
    docsFooter: 'RecipeCatの自己点検で作った一覧です。認証の結果ではありません。',
    quoteHalal: fill('ハラール(販売国: {market})'),
    quoteHaccp: fill('HACCP(食品類型: {type})'),
  },
  zh: {
    pickerTitle: '销售与认证目标（选填）',
    pickerHelp:
      '选择后会按该标准设计配方和工艺，并告诉您还剩什么要做。是否发证由生产工厂申请，认证机构审核决定。',
    halalSub: '去掉猪肉和酒类原料，并标出需要文件证明的原料',
    halalSubAnalyze: '找出猪肉和酒类原料并告知，同时标出需要文件证明的原料',
    haccpSub: '把韩国食品法典标准和控制点（加热、金属检测）放进工艺',
    haccpSubAnalyze: '对照韩国食品法典标准和控制点（加热、金属检测）检查工艺',
    haccpDisabled: 'HACCP是韩国国内销售标准，不适用于在美国销售的产品',
    halalMarketQ: '计划在哪个国家销售？',
    halalMarketHelp: '各国认可的认证机构不同。如不确定，请保留"尚未确定"。',
    cardTitle: '认证准备度',
    lineOk: fill('按{target}标准设计 · 待确认{n}项'),
    lineUnchecked: fill('{target}标准 · 无应用判定项目 · 待确认{n}项'),
    lineImprove: fill('{target}标准待完善{k}项 · 待确认{n}项'),
    lineFix: fill('{target}标准须修改{k}项 · 待确认{n}项'),
    checked: fill('应用检查的{a}项中已反映{b}项'),
    more: '查看详情',
    foot: '这是自我检查结果，不是认证结果。',
    stale: '您修改了原料或工艺，部分项目需要重新确认',
    sheetTitle: fill('{target}准备度'),
    basis: fill('规则基准日 {date} · 适用于本产品的规则{n}条'),
    secFix: '设计中须修改',
    secReflected: '已反映在设计中（应用已检查）',
    secDocs: '须通过工厂和文件确认',
    secReview: '需要专家确认',
    secNa: fill('不适用于本产品的规则{n}条'),
    who: '负责方',
    byAi: 'AI确认',
    staleBadge: '需重新确认',
    stepLabel: fill('第{n}道工序'),
    btnFix: '这样修改',
    btnShareDocs: '分享向工厂索取的文件清单',
    shared: '已分享。',
    notice: fill(
      '此页面是RecipeCat将产品设计与公开的法规和标准进行对照的自我检查。能否发证由生产工厂申请，认证机构（清真为各清真认证机构，HACCP为韩国食品安全管理认证院）审核决定。✅表示"已反映在设计中"，不是审核结果。取得证书前，请勿在标签或商品页面上使用认证标志或"认证"字样。规则以{date}为准，可能会变更。',
    ),
    halalNotice:
      '清真方面，各国认可的认证机构不同。确定销售国后，请在该国名单中确认工厂的认证机构。',
    fixHeader: fill('请按{target}标准修改以下内容。'),
    docsHeader: fill('{product} · {target}准备 — 须向工厂索取的文件'),
    docsIngredients: fill('需证明的原料：{names}'),
    docsFooter: '此清单由RecipeCat自我检查生成，不是认证结果。',
    quoteHalal: fill('清真（销售国：{market}）'),
    quoteHaccp: fill('HACCP（食品类型：{type}）'),
  },
}

// ── 문구 고르기 ───────────────────────────────────────────────────

/** 규칙 문구 [제목, 설명, 할 일]. 변형(variant)이 있으면 설명만 바꾼다. 문구가 없는 규칙은 규칙 id 를 제목으로 */
export function certRuleCopy(item: Pick<CertItem, 'ruleId' | 'variant'>, lang: Language): RuleCopy {
  const base = CERT_RULE_COPY[item.ruleId]?.[lang] ?? [item.ruleId, '', '']
  const variant = item.variant
    ? CERT_VARIANT_COPY[`${item.ruleId}:${item.variant}`]?.[lang]
    : undefined
  return variant ? [base[0], variant, base[2]] : base
}

/** 항목의 해결 문장: 대체 원료 표가 있으면 그것, 없으면 규칙의 할 일 */
export function certItemFix(
  item: Pick<CertItem, 'ruleId' | 'variant' | 'hintKey'>,
  lang: Language,
): string {
  const hint = item.hintKey ? SUBSTITUTIONS[item.hintKey as SubstitutionKey]?.[lang] : undefined
  return hint ?? certRuleCopy(item, lang)[2]
}

/** 결과 카드의 목표 한 줄 (설계 3-2 머리 문장) */
export function certCardLine(t: CertReadinessTarget, lang: Language): string {
  const ui = CERT_UI_COPY[lang]
  const h = readinessHeadline(t)
  const target = CERT_TARGET_LABELS[t.id][lang]
  // 앱이 판정한 항목이 하나도 없으면 '기준에 맞춘 설계' 라고 하지 않는다
  if (h.kind === 'ok')
    return (h.checked > 0 ? ui.lineOk : ui.lineUnchecked)({ target, n: h.confirmCount })
  return (h.kind === 'fix' ? ui.lineFix : ui.lineImprove)({
    target,
    k: h.fixCount,
    n: h.confirmCount,
  })
}

/** "앱이 점검한 a개 중 b개 반영" (점검한 항목이 없으면 null — 줄을 그리지 않는다) */
export function certCheckedLine(t: CertReadinessTarget, lang: Language): string | null {
  const h = readinessHeadline(t)
  return h.checked > 0 ? CERT_UI_COPY[lang].checked({ a: h.checked, b: h.reflected }) : null
}

export const certSheetBasis = (t: CertReadinessTarget, lang: Language) =>
  CERT_UI_COPY[lang].basis({ date: CERT_RULES_READ_DATE, n: t.items.length })

export const certNotice = (lang: Language) =>
  CERT_UI_COPY[lang].notice({ date: CERT_RULES_READ_DATE })

interface NamedIngredient {
  id: string
  name: string
}
const namesOf = (item: CertItem, ingredients: readonly NamedIngredient[]) =>
  (item.ingredientIds ?? [])
    .map((id) => ingredients.find((i) => i.id === id)?.name)
    .filter((n): n is string => !!n)

/**
 * "이렇게 고쳐 줘" 가 AI 수정 입력창에 채울 글 (사용자가 보내야 돌아간다 — 자동으로 보내지 않는다).
 * ❌ 항목마다 "- 제목: 재료 → 해결 문장". 고칠 것이 없으면 null
 */
export function certFixRequestText(
  readiness: CertReadiness,
  ingredients: readonly NamedIngredient[],
  lang: Language,
): string | null {
  const blocks = readiness.targets.flatMap((t) => {
    const fixes = t.items.filter((i) => i.status === 'fix')
    if (fixes.length === 0) return []
    const lines = fixes.map((item) => {
      const [title] = certRuleCopy(item, lang)
      const names = namesOf(item, ingredients)
      return `- ${title}${names.length ? `: ${names.join(', ')}` : ''} → ${certItemFix(item, lang)}`
    })
    return [
      [CERT_UI_COPY[lang].fixHeader({ target: CERT_TARGET_LABELS[t.id][lang] }), ...lines].join(
        '\n',
      ),
    ]
  })
  return blocks.length ? blocks.join('\n\n') : null
}

/** "공장에 받을 서류 목록 공유" 글. ⚠️ 공장 항목(누가 = 공장·수입자)과 증빙할 원료. 앱이 공장에 직접 보내지 않는다 */
export function certDocsListText(
  readiness: CertReadiness,
  product: { name: string; ingredients: readonly NamedIngredient[] },
  lang: Language,
): string | null {
  const ui = CERT_UI_COPY[lang]
  const blocks = readiness.targets.flatMap((t) => {
    const docs = t.items.filter(
      (i) => i.status === 'docs' && (i.who === 'factory' || i.who === 'importer'),
    )
    if (docs.length === 0) return []
    const target = CERT_TARGET_LABELS[t.id][lang]
    const lines = docs.map((item) => {
      const [title, , todo] = certRuleCopy(item, lang)
      const names = namesOf(item, product.ingredients)
      return `- ${title}: ${todo}${names.length ? ` (${names.join(', ')})` : ''}`
    })
    const ingredientNames = [...new Set(docs.flatMap((d) => namesOf(d, product.ingredients)))]
    return [
      [
        ui.docsHeader({ product: product.name, target }),
        ...lines,
        ...(ingredientNames.length
          ? [ui.docsIngredients({ names: ingredientNames.join(', ') })]
          : []),
      ].join('\n'),
    ]
  })
  return blocks.length ? [...blocks, ui.docsFooter].join('\n\n') : null
}

/** 견적 요청서 '필요한 인증' 칸 미리 채우기 (quotes.ts certifications, 한 칸 40자) */
export function certQuoteCertifications(
  readiness: CertReadiness | null | undefined,
  foodType: string,
  lang: Language,
): string[] {
  if (!readiness) return []
  const ui = CERT_UI_COPY[lang]
  return readiness.targets
    .map((t) =>
      t.id === 'halal'
        ? ui.quoteHalal({ market: HALAL_MARKET_LABELS[t.halalMarket ?? 'undecided'][lang] })
        : t.id === 'haccp'
          ? ui.quoteHaccp({ type: foodType.trim() || '-' })
          : CERT_TARGET_LABELS[t.id][lang],
    )
    .map((s) => s.slice(0, 40))
}
